import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMemberEntry } from '../members/memberMemory';
import api from '../../api/api';
import { useUserStore } from '../../stores/userStore';
import { archiveKey, archivePayload, defaultArchivePeriod } from './archiveModel';
export function useArchiveQuery(id, period, enabled) {
  return useQuery({
    queryKey: archiveKey(id, period),
    queryFn: async ({ signal }) =>
      archivePayload(await api.getAuth(`/me/archive?periodType=${period}`, { signal }), period),
    enabled,
    retry: false,
    gcTime: 0,
  });
}
export default function useMyArchive(profile) {
  const user = useUserStore((s) => s.user);
  const enabled =
    user?.id != null &&
    String(user.id) === String(profile.id) &&
    user.nickname === profile.nickname;
  const { entry, location } = useMemberEntry('archive-period60');
  const previous = useRef();
  const [, renderSelection] = useState(0);
  if (previous.current?.entry !== entry) {
    // Opening the goal sheet pushes a URL but retains the parent's selection.
    // Other visits get independent entries; account/token changes invalidate both.
    if (
      ['WEEK', 'MONTH'].includes(new URLSearchParams(location.search).get('goal')) &&
      location.state?.archiveGoalSourceKey === previous.current?.key &&
      previous.current?.entry.current()
    )
      entry.archivePeriod ??= previous.current.entry.archivePeriod;
    previous.current = { entry, key: location.key };
  }
  const week = useArchiveQuery(user?.id, 'WEEK', enabled),
    month = useArchiveQuery(user?.id, 'MONTH', enabled);
  const explicit = entry.current() ? entry.archivePeriod : undefined;
  const period = explicit ?? defaultArchivePeriod(week.data, month.data),
    selected = period === 'WEEK' ? week : month;
  const resolving = !explicit && (week.isPending || month.isPending);
  const unresolvedError =
    !explicit && (week.isError || month.isError) && !selected.data?.goal.isConfigured;
  return {
    enabled,
    id: user?.id,
    period,
    setPeriod: (period) => {
      if (!enabled || !entry.current() || !['WEEK', 'MONTH'].includes(period)) return;
      entry.archivePeriod = period;
      renderSelection((n) => n + 1);
    },
    data: resolving || unresolvedError ? undefined : selected.data,
    loading: enabled && (resolving || selected.isPending),
    error: unresolvedError || selected.isError,
    retry: () => Promise.all([week.refetch(), month.refetch()]),
  };
}
